import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Alert, Box, Typography } from '@mui/material'

import { useMotivationDetails } from '../../hooks/useMotivationDetails'
import { useUAInsightsMeta } from '../../hooks/useUAInsightsMeta'
import { useUAInsightsNavigation } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { CreativeAdsTable } from '../CreativeAdsTable/CreativeAdsTable'
import { GameCreativeTagsTable } from '../GameCreativeTagsTable/GameCreativeTagsTable'
import { TableSkeleton } from '../TableSkeleton/TableSkeleton'
import { UAInsightsDialog } from '../UAInsightsDialog/UAInsightsDialog'

export const MotivationDetailsDialog: FC = () => {
  const { t } = useTranslation('ua-insights')
  const { params, close, canGoBack, goBack } = useUAInsightsNavigation()
  const { motivationLabel } = useUATaxonomy()
  const { motivationById } = useUAInsightsMeta()
  const open = params.dialog === 'motivation'
  const motivationId = open ? params.motivationId : null
  const { data, isLoading, isError, refetch } = useMotivationDetails(motivationId)

  const motivation = data?.motivation ?? (motivationId ? motivationById(motivationId) : undefined)
  const titleLabel = motivation ? motivationLabel(motivation) : null
  const hasCreatives = !!data && data.creatives.length > 0

  return (
    <UAInsightsDialog
      open={open}
      onClose={close}
      title={titleLabel ? `${t('motivation')}: ${titleLabel}` : t('motivation_details')}
      canGoBack={canGoBack}
      onGoBack={goBack}
      showNetworkSelector
      hasData={!!data}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      loadingPlaceholder={<TableSkeleton rows={8} columns={5} />}
      notFoundFallback={
        <Typography variant="body2" color="text.secondary">
          {t('motivation_details_not_found')}
        </Typography>
      }
    >
      {data && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {!hasCreatives && (
            <Alert severity="info" icon={<InfoOutlinedIcon fontSize="inherit" />}>
              {t('motivation_blind_spot_empty_state', {
                motivation: motivationLabel(data.motivation),
                defaultValue:
                  'No creatives currently feature {{motivation}} as a dominant motivation. This is a potential Blind Spot — consider creating ads that appeal to it.',
              })}
            </Alert>
          )}
          {hasCreatives && data.tags.length > 0 && <GameCreativeTagsTable tags={data.tags} totalAds={data.creatives.length} />}
          {hasCreatives && <CreativeAdsTable rows={data.creatives} />}
        </Box>
      )}
    </UAInsightsDialog>
  )
}
